import { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import styles from './LessonLibrary.module.scss';
import { SllProgram, useGetAllProgramsLazyQuery, useGetProgramLazyQuery, useGetProgramsLazyQuery } from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import Header from 'src/components/header/Header';
import { useHistory, useLocation } from 'react-router-dom';
import { generateSlug, resetGoogleTranslateWidget, sortByPosition } from '@src/utils/helpers';
import GradeItemForMultiCollection from './gradeItem/GradeItemForMultiCollection';
import ProgramNavLayout from './navLayout/ProgramNavLayout';
import { useQueryParams, useUser } from '@src/hooks';
import UnitProgram from '@src/components/unit-programs/UnitProgram';
import { AusStudentVoiceSeries } from '@src/utils/constants';
import { ProgramID } from '@src/constants';

function ProgramDetails() {
  const history = useHistory();

  const params = useQueryParams();
  const programId = params.get('pid');

  const location = useLocation<{program: SllProgram}>();
  // const [program, setProgram] = useState(location.state?.program);
  const [user] = useUser();

  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clickedAnywhere, setClickedAnywhere] = useState(false);
  const [movedScroll, setMovedScroll] = useState(false);
  const [loadOtherMCs, setLoadOtherMCs] = useState(0);
  useEffect(() => {
    const handlePageHide = () => {
      sessionStorage.setItem('isReloading', 'true');
    };
  
    window.addEventListener('pagehide', handlePageHide);
  
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    if (!programId) { // If there is no PID, send back to programs page
      history.replace("/lessons?tab=programs")
    }
  }, [programId, history])

  const isAUuser = useMemo(() => {return user?.id?.startsWith("AU_") ? true : false}, [user?.id]);

  const [getPrograms, { data, loading }] = useGetProgramsLazyQuery({
    fetchPolicy: "cache-first",
  });
  const [getAllPrograms, { data: unitsData, loading: unitLoading}] = useGetAllProgramsLazyQuery({ fetchPolicy: 'network-only' })
  const [getProgram, { data: programData, loading: programLoading}] = useGetProgramLazyQuery({ fetchPolicy: 'network-only' })
  
  const program = useMemo(() => {
    if (location.state?.program) {
      return location.state.program;
    } else if (programId === "studentvoiceseries") {
      return AusStudentVoiceSeries;
    } else if (programId === ProgramID.EDUCATORONBOARDING || programId === ProgramID.PROFESSIONALDEV || programId === ProgramID.AUPROFESSIONALDEV) {
      return programData?.getProgram as SllProgram;
    } else {
      return data?.getPrograms?.find(prog => prog.id === programId) as SllProgram;
    }
  }, [location.state, data?.getPrograms, programId, programData]);

  useEffect(() => {
    if (programId === "studentvoiceseries" ) { // AUS Unit programs
      const isReloading = sessionStorage.getItem('isReloading');

      if (isReloading) {
        sessionStorage.removeItem('isReloading');
        setTimeout(() => {
          getAllPrograms();
        }, 500);
      } else {
        getAllPrograms();
      }
    } else if (programId === ProgramID.EDUCATORONBOARDING || programId === ProgramID.PROFESSIONALDEV || programId === ProgramID.AUPROFESSIONALDEV) {
      getProgram({
        variables: {
          input: {
            id: programId
          }
        }
      })
    } else {
      getPrograms();
    }
  }, [program, programId, getPrograms, getAllPrograms, getProgram]);

  useEffect(() => {
    resetGoogleTranslateWidget();
  }, [])

  // Scroll to the top of the list when the component mounts
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      setTimeout(() => {
        root.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, []);

  const handleScroll = () => {
    setMovedScroll(current => !current);
  };

  useEffect(() => {
    const listViewElement  = document.getElementById("lessonListView")
    if (listViewElement) {
      setTimeout(() => {
        listViewElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 500)
    }
  }, [])

  const handleSelect = useCallback((gradeId: string) => {
    if (selectedId === gradeId) {
      setSelected(false);
      setSelectedId(null);
    } else {
      setSelected(true);
      setSelectedId(gradeId);
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }, [selectedId, setSelected, setSelectedId])

  const programList = useMemo(() => {
    if (!data?.getPrograms) return [];
    let filterPrograms = [...data.getPrograms];
    if (isAUuser) {
      filterPrograms.push(AusStudentVoiceSeries);
    }
    return filterPrograms.filter((program) => !!program?.isShown);
  }, [isAUuser, data]);

  const handleClickAnywhere = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setClickedAnywhere(true);
  }

  const handleBackToTop = (gradeId?: string) => {
    if (gradeId) {
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }

  const handleSelectProgram = useCallback((program: SllProgram) => {
    setLoadOtherMCs(0);
    history.push(`/program/${generateSlug(program.name)}?pid=${program.id}`, { program: program });
  }, [history]);

  const multiCollectionList = useMemo(() => {
    if (!program?.mcs) return [];
    return [...program?.mcs ?? []].sort(sortByPosition);
  }, [program?.mcs])

  const mainViewStyle = useMemo(() => {
    const classes = [styles.libraryView];

    return classes.join(' ');
  }, [])

  const lessonListView = (
    <div style={{width: '100%', height: '100%'}}>
      {program && <ProgramNavLayout program={program} allPrograms={programList as SllProgram[]} handleSelectProgram={handleSelectProgram} movedScroll={movedScroll} isAUuser={isAUuser} />}
      <div className={styles.mainView}>
        <div className={mainViewStyle} onClick={handleClickAnywhere}>
          <div className={styles.listView}>
          {(programId === "studentvoiceseries") ? (
            (unitLoading) ? <div className={styles.lazyLoadingView}><Spinner /></div> : <UnitProgram courses={unitsData?.programs as any} isPrimary={false} />
          ) : (
            (loading || programLoading) ? <div className={styles.lazyLoadingView}><Spinner /></div> : 
            multiCollectionList.map((item, index) => {
              return (
                <Suspense key={index} fallback={<div className={styles.lazyLoadingView}><Spinner /></div>}>
                  <div style={{position: 'relative'}}>
                    <GradeItemForMultiCollection
                      MCId={item?.id ?? ""}
                      MCName={item?.name ?? ""} 
                      isMCSelected={selected}
                      selectedMCId={selectedId}
                      handleMCSelect={handleSelect} 
                      handleBackToTop={() => handleBackToTop(item?.name)}
                      clickedAnywhere={clickedAnywhere}
                      handleSelectCollection={()=>setClickedAnywhere(false)}
                      programId={program?.id ?? ''}
                      MCGradeName={item?.grade ?? ""}
                      programName={program?.name ?? ''}
                      isAUuser={isAUuser}
                      loadOthers={loadOtherMCs}
                      setLoadOthers={setLoadOtherMCs}
                      index={index}
                    />
                  </div>
                </Suspense>
              )
            })
          )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <Header />
      <div className={styles.wrapper} onScroll={handleScroll}>
        <div className={styles.page}>
          {lessonListView}
        </div>
      </div>
    </>
  );
}

export default ProgramDetails;
